import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
import { getVxEstado } from '../components/tanStackColumns';
import { atualizarProgramacaoHavan, buscarMotoristasProgramacaoHavan, buscarOpcoesCargaVxProgramacaoHavan, buscarPreparacaoVxProgramacaoHavan, buscarVeiculosProgramacaoHavan, buscarConjuntoVeiculoProgramacaoHavan, buscarConjuntoMotoristaProgramacaoHavan, baixarLaudoVxProgramacaoHavan, aplicarDefinitivaProgramacaoHavan, consultarVxProgramacaoHavan, importarProgramacaoHavan, listarProgramacaoHavan, listarProgramacaoHavanDatas, previsualizarDefinitivaProgramacaoHavan, obterConfiguracaoCadastroTms, cadastrarMotoristaTms, cadastrarVeiculoTms, atualizarObservacaoProgramacaoHavan, atualizarProtocoloProgramacaoHavan, atualizarGestorAvisadoProgramacaoHavan } from '../../services/programacaoHavanService';

// O turno vem inteiro (uma data da Havan tem ~60 cargas por turno): filtro, ordenação e paginação da tabela valem para o turno todo.
// A paginação do servidor só entra se um turno passar disso.
const PAGE_SIZE = 300;
// Todas as datas ficam disponíveis: a tela mostra as mais recentes como botões e as demais pela busca de data.

const createTurnoState = (pagina = 1) => ({ registros: [], pagina, tamanhoPagina: PAGE_SIZE, totalRegistros: 0, totalPaginas: 0, carregando: false, erro: null });

export const formatDateOption = ({ data_programacao, definitiva, definitiva_aplicada_em, definitiva_usuario }) => {
  const date = String(data_programacao || '').slice(0, 10);
  const [year, month, day] = date.split('-');
  const weekday = new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' });

  return {
    value: date,
    label: [day, month, year].filter(Boolean).join('/'),
    diaSemana: weekday.toUpperCase(),
    definitiva: Boolean(definitiva),
    definitivaAplicadaEm: definitiva_aplicada_em || null,
    definitivaUsuario: definitiva_usuario || null
  };
};

// alteracoes_definitiva vem como texto JSON: [{ campo, titulo, anterior, novo }].
const lerAlteracoesDefinitiva = valor => {
  if (Array.isArray(valor)) return valor;
  try {
    const alteracoes = JSON.parse(valor || '[]');
    return Array.isArray(alteracoes) ? alteracoes : [];
  } catch {
    return [];
  }
};

const mapearRegistro = (registro, turno) => ({
  ...registro,
  turno,
  ct: registro.numero_carga || '',
  filial: [registro.filial_nome, registro.uf].filter(Boolean).join(' · '),
  alteracoesDefinitiva: lerAlteracoesDefinitiva(registro.alteracoes_definitiva),
  nome: registro.nome_motorista || '',
  cpf: registro.cpf_motorista || '',
  telefoneMotorista: registro.telefone_motorista || '',
  placas: [registro.placa_cavalo, registro.placa_carreta_1, registro.placa_carreta_2, registro.placa_carreta_3].filter(Boolean)
});

const getErrorMessage = error => error?.response?.data?.error || error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Não foi possível carregar este turno.';
const readFileAsBase64 = file => new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result || '').split(',').pop()); reader.onerror = reject; reader.readAsDataURL(file); });

// Campos do motorista e do conjunto: mexer em qualquer um faz o operador "assumir" o grupo (motorista_manual /
// conjunto_manual) e, daí em diante, o vazio fica vazio em vez de voltar a mostrar o que está na carga do TMS.
const CAMPOS_MOTORISTA = ['codigo_motorista_tms', 'cpf_motorista', 'nome_motorista', 'telefone_motorista'];
const CAMPOS_CONJUNTO = ['codigo_veiculo_tms', 'numero_frota', 'placa_cavalo', 'placa_carreta_1', 'placa_carreta_2', 'placa_carreta_3'];
const mexeuEm = (campos, campo, alteracoes) => campos.includes(campo) || campos.some(nome => nome in alteracoes);

// 'in': valor explícito em alteracoes vale, inclusive nulo (apagar, ou código limpo do motorista/cavalo fora do TMS);
// com ?? o valor anterior ficaria na linha.
const escolher = (alteracoes, nome, padrao) => (nome in alteracoes ? alteracoes[nome] : padrao);

const criarPayloadAtualizacao = (registro, campo, valor, alteracoes = {}) => ({
  codigo_motorista_tms: escolher(alteracoes, 'codigo_motorista_tms', registro.codigo_motorista_tms ?? null),
  cpf_motorista: escolher(alteracoes, 'cpf_motorista', registro.cpf_motorista || null),
  nome_motorista: escolher(alteracoes, 'nome_motorista', campo === 'nome_motorista' ? valor || null : registro.nome_motorista || registro.nome || null),
  telefone_motorista: escolher(alteracoes, 'telefone_motorista', registro.telefone_motorista || registro.telefoneMotorista || null),
  numero_frota: escolher(alteracoes, 'numero_frota', campo === 'numero_frota' ? valor || null : registro.numero_frota || null),
  codigo_veiculo_tms: escolher(alteracoes, 'codigo_veiculo_tms', registro.codigo_veiculo_tms ?? null),
  placa_cavalo: escolher(alteracoes, 'placa_cavalo', campo === 'placa_cavalo' ? valor || null : registro.placa_cavalo || null),
  placa_carreta_1: 'placa_carreta_1' in alteracoes ? alteracoes.placa_carreta_1 : campo === 'placa_carreta_1' ? valor || null : registro.placa_carreta_1 || null,
  placa_carreta_2: 'placa_carreta_2' in alteracoes ? alteracoes.placa_carreta_2 : campo === 'placa_carreta_2' ? valor || null : registro.placa_carreta_2 || null,
  placa_carreta_3: 'placa_carreta_3' in alteracoes ? alteracoes.placa_carreta_3 : campo === 'placa_carreta_3' ? valor || null : registro.placa_carreta_3 || null,
  terceiro: alteracoes.terceiro ?? Boolean(registro.terceiro),
  vx: Boolean(registro.vx),
  tms: Boolean(registro.tms),
  frota: Boolean(registro.frota),
  cancelado: Boolean(registro.cancelado),
  motorista_manual: mexeuEm(CAMPOS_MOTORISTA, campo, alteracoes),
  conjunto_manual: mexeuEm(CAMPOS_CONJUNTO, campo, alteracoes)
});

// Observação livre do operador: não é motorista nem conjunto, grava pelo endpoint próprio.
const CAMPO_OBSERVACAO = 'observacao_operador';

const CONFIRMACAO_FECHADA = { show: false, titulo: '', mensagem: '', detalhe: '', executar: null };
const MOTORISTA_FORA_TMS_FECHADO = { show: false, modo: 'usar', registro: null, cpf: '', nome: '', telefone: '' };
const VEICULO_TMS_FECHADO = { show: false, registro: null, campo: '', placa: '' };
const CADASTRO_TMS_DESLIGADO = { habilitado: false, ambiente: '' };

// Placa antiga (ABC1234) ou Mercosul (ABC1D23).
export const normalizarPlacaDigitada = valor => String(valor || '').replace(/[^a-z0-9]/gi, '').toUpperCase();
export const placaValida = placa => /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(normalizarPlacaDigitada(placa));

// Dígitos verificadores do CPF (mesma regra do backend).
export const cpfValido = valor => {
  const digitos = String(valor || '').replace(/\D/g, '');
  if (digitos.length !== 11 || /^(\d)\1{10}$/.test(digitos)) return false;
  const digito = quantidade => {
    const soma = digitos.slice(0, quantidade).split('').reduce((total, numero, indice) => total + Number(numero) * (quantidade + 1 - indice), 0);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return digito(9) === Number(digitos[9]) && digito(10) === Number(digitos[10]);
};

const somenteDigitos = valor => String(valor || '').replace(/\D/g, '');
const conjuntoDePlacas = dados => [dados.placa_cavalo, dados.placa_carreta_1, dados.placa_carreta_2, dados.placa_carreta_3]
  .map(placa => String(placa || '').replace(/[^a-z0-9]/gi, '').toUpperCase())
  .filter(Boolean)
  .sort()
  .join('|');

/**
 * Aviso quando a alteração troca motorista ou veículo de uma carga com pesquisa VX concluída ou em andamento.
 * Mesma regra do backend: CPF diferente ou qualquer mudança nas placas (inclusive remover carreta).
 */
export const avisoAlteracaoComVx = (registro, payload) => {
  if (!['valida', 'andamento'].includes(getVxEstado(registro))) return null;
  const mudouMotorista = somenteDigitos(registro.cpf_motorista) !== somenteDigitos(payload.cpf_motorista);
  if (!mudouMotorista && conjuntoDePlacas(registro) === conjuntoDePlacas(payload)) return null;

  const status = registro.vx_status || 'em andamento';
  if (/biometria/i.test(status)) {
    return {
      titulo: 'Pesquisa VX em biometria',
      mensagem: 'Nessa etapa a pesquisa já está em andamento e não pode ser cancelada, porém você pode alterar o motorista ou veículo ainda. Deseja continuar?',
      detalhe: 'Depois da alteração, a carga volta para "Consultar VX" para você fazer uma nova consulta com os dados atualizados.'
    };
  }
  return {
    titulo: 'Carga com consulta VX',
    mensagem: `Esta carga tem uma consulta VX (${status}). Ao alterar o motorista ou o veículo, a pesquisa será cancelada na VX. Deseja continuar?`,
    detalhe: 'Depois da alteração, a carga volta para "Consultar VX" e o laudo anterior deixa de aparecer. A nova consulta não é feita automaticamente.'
  };
};

export const useProgramacaoHavanListagem = () => {
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [turnoAtivo, setTurnoAtivo] = useState(null);
  const [importModal, setImportModal] = useState({ show: false });
  const [inlineEditingCell, setInlineEditingCell] = useState(null);
  const [updatingKey, setUpdatingKey] = useState(null);
  const [terceiroSalvandoIds, setTerceiroSalvandoIds] = useState([]);
  const [protocoloSalvandoIds, setProtocoloSalvandoIds] = useState([]);
  const [vxConfirmacaoAlteracao, setVxConfirmacaoAlteracao] = useState(CONFIRMACAO_FECHADA);
  const [motoristaForaTms, setMotoristaForaTms] = useState(MOTORISTA_FORA_TMS_FECHADO);
  const [veiculoTms, setVeiculoTms] = useState(VEICULO_TMS_FECHADO);
  const [cadastroTms, setCadastroTms] = useState(CADASTRO_TMS_DESLIGADO);
  const [vxModal, setVxModal] = useState({ show: false, row: null, reason: '' });
  const [vxLoading, setVxLoading] = useState(false);
  const [vxConsultandoId, setVxConsultandoId] = useState(null);
  const [vxBaixandoLaudoId, setVxBaixandoLaudoId] = useState(null);
  const [vxResult, setVxResult] = useState(null);
  const [vxUnavailable, setVxUnavailable] = useState({ show: false, row: null, message: '' });
  const [vxPesquisa, setVxPesquisa] = useState({ show: false, row: null, data: null, erro: '' });
  const [vxOpcoesCarga, setVxOpcoesCarga] = useState({ data: null, carregando: false, erro: '' });
  const [turnos, setTurnos] = useState({ primeiro: createTurnoState(), segundo: createTurnoState() });
  const requestIdsRef = useRef({ primeiro: 0, segundo: 0 });
  const buscasEdicaoRef = useRef(new Map());
  const selectedDateRef = useRef(null);
  const suppressNextDateLoadRef = useRef(false);

  const selectedDateOption = useMemo(() => dateOptions.find(option => option.value === selectedDate) || { diaSemana: '' }, [dateOptions, selectedDate]);

  const carregarTurno = useCallback(async (turno, data, pagina) => {
    if (!data) return;

    const chaveTurno = turno === 1 ? 'primeiro' : 'segundo';
    const requestId = ++requestIdsRef.current[chaveTurno];

    setTurnos(previous => ({ ...previous, [chaveTurno]: { ...previous[chaveTurno], pagina, carregando: true, erro: null } }));

    try {
      const resposta = await listarProgramacaoHavan({ data_programacao: data, turno, pagina, tamanho_pagina: PAGE_SIZE });

      if (data !== selectedDateRef.current || requestId !== requestIdsRef.current[chaveTurno]) return;

      setTurnos(previous => ({
        ...previous,
        [chaveTurno]: {
          registros: (resposta.registros || []).map(registro => mapearRegistro(registro, chaveTurno)),
          pagina: resposta.pagina || pagina,
          tamanhoPagina: resposta.tamanho_pagina || PAGE_SIZE,
          totalRegistros: resposta.total_registros || 0,
          totalPaginas: resposta.total_paginas || 0,
          carregando: false,
          erro: null
        }
      }));
    } catch (error) {
      if (data !== selectedDateRef.current || requestId !== requestIdsRef.current[chaveTurno]) return;

      setTurnos(previous => ({ ...previous, [chaveTurno]: { ...previous[chaveTurno], pagina, carregando: false, erro: getErrorMessage(error) } }));
    }
  }, []);

  const carregarData = useCallback(data => Promise.all([carregarTurno(1, data, 1), carregarTurno(2, data, 1)]), [carregarTurno]);

  const carregarDatas = useCallback(async () => {
    const resposta = await listarProgramacaoHavanDatas();
    const opcoes = (resposta.registros || [])
      .map(formatDateOption)
      .sort((first, second) => second.value.localeCompare(first.value));
    setDateOptions(opcoes);
    return opcoes;
  }, []);

  useEffect(() => {
    let ativo = true;

    carregarDatas().then(opcoes => {
      if (!ativo) return;
      setSelectedDate(atual => atual || opcoes[0]?.value || null);
    }).catch(() => {
      if (ativo) notification.error('Não foi possível carregar as datas da programação Havan.');
    });

    return () => { ativo = false; };
  }, [carregarDatas]);

  // Cadastro no TMS é opcional: sem a configuração (ou API antiga), a tela segue sem oferecer.
  useEffect(() => {
    let ativo = true;
    Promise.resolve()
      .then(() => obterConfiguracaoCadastroTms?.())
      .then(configuracao => {
        if (ativo && configuracao) setCadastroTms({ habilitado: Boolean(configuracao.habilitado), ambiente: configuracao.ambiente || '' });
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;
    if (suppressNextDateLoadRef.current) {
      suppressNextDateLoadRef.current = false;
      return;
    }
    carregarData(selectedDate);
  }, [carregarData, selectedDate]);

  const handleSelectDate = useCallback(date => {
    selectedDateRef.current = date;
    setSelectedDate(date);
    setTurnos({ primeiro: createTurnoState(), segundo: createTurnoState() });
  }, []);

  const handleNextDate = useCallback(() => {
    const currentIndex = dateOptions.findIndex(option => option.value === selectedDate);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % dateOptions.length : 0;
    handleSelectDate(dateOptions[nextIndex]?.value || null);
  }, [dateOptions, handleSelectDate, selectedDate]);

  const handlePreviousDate = useCallback(() => {
    const currentIndex = dateOptions.findIndex(option => option.value === selectedDate);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : dateOptions.length - 1;
    handleSelectDate(dateOptions[previousIndex]?.value || null);
  }, [dateOptions, handleSelectDate, selectedDate]);

  const handleTurnoFilter = useCallback(turno => setTurnoAtivo(current => (current === turno ? null : turno)), []);

  const handleChangePage = useCallback((chaveTurno, pagina) => {
    const turnoAtual = turnos[chaveTurno];
    if (!selectedDate || !turnoAtual || pagina < 1 || pagina > turnoAtual.totalPaginas || pagina === turnoAtual.pagina) return;
    carregarTurno(chaveTurno === 'primeiro' ? 1 : 2, selectedDate, pagina);
  }, [carregarTurno, selectedDate, turnos]);

  const handleRetryTurno = useCallback(chaveTurno => {
    const turnoAtual = turnos[chaveTurno];
    if (!selectedDate || !turnoAtual) return;
    carregarTurno(chaveTurno === 'primeiro' ? 1 : 2, selectedDate, turnoAtual.pagina);
  }, [carregarTurno, selectedDate, turnos]);

  const handleOpenImportModal = useCallback(() => setImportModal({ show: true }), []);

  const handleCloseImportModal = useCallback(() => setImportModal({ show: false }), []);

  // Depois de gravar: recarrega as datas e mostra a data importada (ou mantém a atual, se ela ainda existir).
  const recarregarAposImportacao = useCallback(async (dataPreferida = null) => {
    const opcoes = await carregarDatas();
    const existe = data => Boolean(data) && opcoes.some(opcao => opcao.value === data);
    const dataParaCarregar = existe(dataPreferida) ? dataPreferida : existe(selectedDateRef.current) ? selectedDateRef.current : opcoes[0]?.value || null;

    selectedDateRef.current = dataParaCarregar;
    setTurnos({ primeiro: createTurnoState(), segundo: createTurnoState() });

    if (dataParaCarregar !== selectedDate) {
      suppressNextDateLoadRef.current = true;
      setSelectedDate(dataParaCarregar);
    }

    if (dataParaCarregar) await carregarData(dataParaCarregar);
  }, [carregarData, carregarDatas, selectedDate]);

  const handleImportarArquivo = useCallback(async file => {
    const resultado = await importarProgramacaoHavan(file);
    await recarregarAposImportacao();
    notification.success('Importação da prévia da programação Havan concluída.');
    return resultado;
  }, [recarregarAposImportacao]);

  const handlePrevisualizarDefinitiva = useCallback(file => previsualizarDefinitivaProgramacaoHavan(file), []);

  const handleAplicarDefinitiva = useCallback(async file => {
    const resultado = await aplicarDefinitivaProgramacaoHavan(file);
    await recarregarAposImportacao(String(resultado?.data_programacao || '').slice(0, 10) || null);
    notification.success('Programação definitiva aplicada.');
    return resultado;
  }, [recarregarAposImportacao]);

  const handleIniciarEdicaoInline = useCallback((campo, registro, popoverPosition = null) => {
    if (!registro?.id || inlineEditingCell || updatingKey) return;

    const valores = {
      nome_motorista: registro.nome_motorista || registro.nome || '',
      numero_frota: registro.numero_frota || '',
      placa_cavalo: registro.placa_cavalo || '',
      placa_carreta_1: registro.placa_carreta_1 || '',
      placa_carreta_2: registro.placa_carreta_2 || '',
      placa_carreta_3: registro.placa_carreta_3 || '',
      observacao_operador: registro.observacao_operador || ''
    };

    setInlineEditingCell({
      id: registro.id,
      campo,
      valorOriginal: valores[campo] || '',
      popoverPosition
    });
  }, [inlineEditingCell, updatingKey]);

  const handleCancelarEdicaoInline = useCallback(() => {
    if (!updatingKey) setInlineEditingCell(null);
  }, [updatingKey]);

  const handleBuscarOpcoesEdicao = useCallback(async (campo, registro, termo) => {
    if (!registro?.id) return [];
    const chave = `${registro.id}:${campo}`;
    const texto = String(termo || '').trim();
    const identificadorBusca = `${chave}:${texto.toUpperCase()}`;
    if (buscasEdicaoRef.current.has(identificadorBusca)) return buscasEdicaoRef.current.get(identificadorBusca);

    try {
      // Frota e Placa cavalo só mostram tração (o cavalo); as carretas só mostram reboques.
      const requisicao = campo === 'nome_motorista'
        ? await buscarMotoristasProgramacaoHavan(registro.id, texto)
        : await buscarVeiculosProgramacaoHavan(registro.id, texto, campo.startsWith('placa_carreta_') ? 'reboque' : 'tracao');
      const todos = Array.isArray(requisicao) ? requisicao : requisicao.registros || [];
      // Frota só existe para veículo do TMS: placas fora do TMS não entram na busca da Frota.
      const registros = campo === 'numero_frota' ? todos.filter(opcao => !opcao.fora_tms) : todos;
      buscasEdicaoRef.current.set(identificadorBusca, registros);
      return registros;
    } catch {
      return [];
    }
  }, []);

  const handleAtualizarCampo = useCallback(async (registro, campo, valor, alteracoes = {}, forcarAtualizacao = false) => {
    if (!registro?.id || !selectedDate) return;

    const chave = `${registro.id}:${campo}`;
    const valorNormalizado = String(valor || '').trim();
    if (inlineEditingCell && (inlineEditingCell.id !== registro.id || inlineEditingCell.campo !== campo)) return;
    if (!forcarAtualizacao && inlineEditingCell && valorNormalizado === String(inlineEditingCell.valorOriginal || '').trim()) {
      setInlineEditingCell(null);
      return;
    }

    const payload = criarPayloadAtualizacao(registro, campo, valorNormalizado, alteracoes);
    const aviso = avisoAlteracaoComVx(registro, payload);

    // true quando a linha foi gravada (ex.: a API recusa reboque no cavalo e tração na carreta).
    const salvar = async () => {
      setUpdatingKey(chave);
      let salvo = false;
      try {
        await atualizarProgramacaoHavan(registro.id, payload);
        salvo = true;
        await carregarTurno(registro.turno === 'segundo' ? 2 : 1, selectedDate, turnos[registro.turno]?.pagina || 1);
        notification.success(aviso
          ? 'Programação atualizada. A consulta VX anterior deixou de valer: faça uma nova consulta com os dados atualizados.'
          : 'Programação atualizada com sucesso.');
      } catch (error) {
        notification.error(getErrorMessage(error));
      } finally {
        setUpdatingKey(null);
        setInlineEditingCell(null);
      }
      return salvo;
    };

    // Troca de motorista/veículo com pesquisa VX concluída ou em andamento: o usuário confirma antes.
    if (aviso) {
      setVxConfirmacaoAlteracao({ show: true, ...aviso, executar: salvar });
      return undefined;
    }
    return salvar();
  }, [carregarTurno, inlineEditingCell, selectedDate, turnos]);

  const handleCancelarAlteracaoComVx = useCallback(() => {
    setVxConfirmacaoAlteracao(CONFIRMACAO_FECHADA);
    setInlineEditingCell(null);
  }, []);

  const handleConfirmarAlteracaoComVx = useCallback(async () => {
    const { executar } = vxConfirmacaoAlteracao;
    setVxConfirmacaoAlteracao(CONFIRMACAO_FECHADA);
    if (executar) await executar();
  }, [vxConfirmacaoAlteracao]);

  const handleSelecionarOpcaoEdicao = useCallback(async (campo, registro, opcao) => {
    if (campo === 'nome_motorista') {
      const alteracoes = {
        codigo_motorista_tms: opcao.codigo_motorista_tms,
        cpf_motorista: opcao.cpf_motorista,
        nome_motorista: opcao.nome_motorista,
        telefone_motorista: opcao.telefone_motorista
      };
      // Motorista com um único cavalo vinculado no TMS: o conjunto dele substitui frota, cavalo e carretas.
      // Com nenhum ou mais de um cavalo, só o motorista muda (o operador escolhe a frota).
      // A sugestão vem do banco (cerca de 2 horas de atraso): o vínculo atual é perguntado ao TMS e, se falhar, vale o do banco.
      let vinculo = opcao;
      if (opcao.codigo_motorista_tms) {
        try {
          const doTms = await buscarConjuntoMotoristaProgramacaoHavan(registro.id, opcao.codigo_motorista_tms);
          if (doTms?.origem === 'tms') vinculo = doTms;
        } catch {
          // Plano B: o vínculo do banco que veio na sugestão.
        }
      }
      if (vinculo.cavalos_vinculados === 1 && vinculo.placa_cavalo) {
        const carretas = (vinculo.carretas || []).filter(Boolean).slice(0, 3);
        Object.assign(alteracoes, {
          codigo_veiculo_tms: vinculo.codigo_veiculo_tms,
          placa_cavalo: vinculo.placa_cavalo,
          numero_frota: vinculo.numero_frota || vinculo.placa_cavalo,
          placa_carreta_1: carretas[0] || null,
          placa_carreta_2: carretas[1] || null,
          placa_carreta_3: carretas[2] || null
        });
      }
      return handleAtualizarCampo(registro, campo, opcao.nome_motorista || '', alteracoes, true);
    }
    // Carreta usa a mesma busca do cavalo, mas não altera cavalo, frota nem código do veículo.
    if (campo.startsWith('placa_carreta_')) {
      return handleAtualizarCampo(registro, campo, opcao.placa_cavalo || '', {}, true);
    }
    // Veículo sem número de frota (terceiro): a placa ocupa o campo Frota, que é a convenção da operação.
    const veiculo = { ...opcao, numero_frota: opcao.numero_frota || opcao.placa_cavalo || '' };
    // Conjunto padrão do cavalo no TMS (reboques do mesmo motorista): preenche Carreta 1, 2 e 3. Sem conjunto, as carretas ficam como estão.
    // A sugestão vem do banco (cerca de 2 horas de atraso); o vínculo atual é perguntado ao TMS na escolha e, se falhar, vale o do banco.
    let carretasDoTms = (opcao.carretas || []).filter(Boolean).slice(0, 3);
    if (!opcao.fora_tms && opcao.placa_cavalo) {
      try {
        const conjunto = await buscarConjuntoVeiculoProgramacaoHavan(registro.id, opcao.placa_cavalo);
        if (Array.isArray(conjunto?.carretas)) carretasDoTms = conjunto.carretas.filter(Boolean).slice(0, 3);
      } catch {
        // Plano B: as carretas do banco que vieram na sugestão.
      }
    }
    if (carretasDoTms.length) {
      [1, 2, 3].forEach(posicao => { veiculo[`placa_carreta_${posicao}`] = carretasDoTms[posicao - 1] || null; });
    }
    return handleAtualizarCampo(registro, campo, campo === 'numero_frota' ? veiculo.numero_frota : veiculo.placa_cavalo || '', veiculo, true);
  }, [handleAtualizarCampo]);

  // Carreta errada: limpa a posição; o backend sobe as carretas seguintes para não deixar buraco no conjunto.
  // Motorista que não vai / cavalo que vai trocar: apaga e fica vazio (não volta o que está na carga do TMS).
  // Frota e placa do cavalo são o mesmo veículo: apagar um limpa os dois; as carretas ficam.
  const handleRemoverCampo = useCallback((campo, registro) => {
    if (campo === 'nome_motorista') {
      return handleAtualizarCampo(registro, campo, '', { codigo_motorista_tms: null, cpf_motorista: null, nome_motorista: null, telefone_motorista: null }, true);
    }
    if (campo === 'numero_frota' || campo === 'placa_cavalo') {
      return handleAtualizarCampo(registro, campo, '', { codigo_veiculo_tms: null, numero_frota: null, placa_cavalo: null }, true);
    }
    return handleAtualizarCampo(registro, campo, '', {}, true);
  }, [handleAtualizarCampo]);

  // Não achou no TMS: a operação não trava. Placa válida é aplicada direto (sem código do TMS);
  // motorista abre um formulário curto porque o CPF é obrigatório (chave da VX e do vínculo futuro com o TMS).
  const handleUsarForaTms = useCallback(async (campo, registro, termo) => {
    setInlineEditingCell(null);
    if (campo === 'nome_motorista') {
      const texto = String(termo || '').trim();
      const ehDocumento = texto !== '' && /^[\d.\-\s]+$/.test(texto);
      setMotoristaForaTms({ show: true, registro, cpf: ehDocumento ? texto.replace(/\D/g, '') : '', nome: ehDocumento ? '' : texto.toUpperCase() });
      return undefined;
    }
    const placa = normalizarPlacaDigitada(termo);
    if (!placaValida(placa)) return undefined;
    // Com o cadastro no TMS ligado, abre o cadastro do veículo (como o motorista) só depois que a linha gravou:
    // placa recusada (ex.: carreta do TMS no cavalo) não pode ir para o TMS com o tipo da célula. Cancelar mantém a placa só na programação.
    const salvo = await handleSelecionarOpcaoEdicao(campo, registro, { codigo_veiculo_tms: null, placa_cavalo: placa, numero_frota: null, carretas: [], fora_tms: true });
    if (salvo === true && cadastroTms.habilitado) setVeiculoTms({ show: true, registro, campo, placa });
    return salvo;
  }, [cadastroTms.habilitado, handleSelecionarOpcaoEdicao]);

  const handleFecharMotoristaForaTms = useCallback(() => setMotoristaForaTms(MOTORISTA_FORA_TMS_FECHADO), []);

  const recarregarTurnoAposTms = useCallback(registro => (selectedDate
    ? carregarTurno(registro?.turno === 'segundo' ? 2 : 1, selectedDate, turnos[registro?.turno]?.pagina || 1)
    : Promise.resolve()), [carregarTurno, selectedDate, turnos]);

  // Cadastro no TMS é um extra: a programação já está salva; recusa/erro do TMS só avisa (com o motivo) e permite tentar de novo.
  const enviarAoTms = useCallback(async (descricao, chamada, registro) => {
    const ambiente = cadastroTms.ambiente === 'producao' ? '' : ` (${cadastroTms.ambiente || 'homologação'})`;
    try {
      const resultado = await chamada();
      if (resultado?.cadastrado) {
        notification.success(`${descricao} cadastrado no TMS${ambiente}. A linha passa a usar o cadastro do TMS depois da sincronização do dia seguinte.`);
      } else {
        notification.error(`O TMS${ambiente} não cadastrou ${descricao.toLowerCase()}: ${resultado?.mensagem || 'motivo não informado'}. A programação continua salva; tente de novo pelo selo "Fora do TMS".`);
      }
      return resultado;
    } catch (error) {
      notification.error(`Não foi possível cadastrar no TMS: ${getErrorMessage(error)}. A programação continua salva.`);
      return null;
    } finally {
      await recarregarTurnoAposTms(registro);
    }
  }, [cadastroTms.ambiente, recarregarTurnoAposTms]);

  // modo 'usar': veio da busca (salva a linha e, se marcado, cadastra no TMS); 'cadastrar': veio do selo (só cadastra no TMS).
  const handleSalvarMotoristaForaTms = useCallback(async ({ cpf, nome, telefone, cadastrarNoTms, terceiro }) => {
    const { registro, modo } = motoristaForaTms;
    setMotoristaForaTms(MOTORISTA_FORA_TMS_FECHADO);
    if (!registro) return;
    if (modo !== 'cadastrar') {
      await handleSelecionarOpcaoEdicao('nome_motorista', registro, {
        codigo_motorista_tms: null,
        cpf_motorista: cpf,
        nome_motorista: nome,
        telefone_motorista: telefone || null,
        cavalos_vinculados: 0,
        fora_tms: true
      });
    }
    if (cadastrarNoTms && cadastroTms.habilitado) {
      await enviarAoTms('Motorista', () => cadastrarMotoristaTms(registro.id, { cpf, nome, telefone: telefone || null, terceiro: terceiro !== false }), registro);
    }
  }, [cadastroTms.habilitado, enviarAoTms, handleSelecionarOpcaoEdicao, motoristaForaTms]);

  // Clique no selo "Fora do TMS": abre o cadastro no TMS já com o que a linha tem.
  const handleAbrirCadastroTms = useCallback((campo, registro) => {
    if (!cadastroTms.habilitado || !registro) return;
    setInlineEditingCell(null);
    if (campo === 'nome_motorista') {
      setMotoristaForaTms({
        show: true,
        modo: 'cadastrar',
        registro,
        cpf: String(registro.cpf_motorista || '').replace(/\D/g, ''),
        nome: registro.nome_motorista || registro.nome || '',
        telefone: registro.telefone_motorista || ''
      });
      return;
    }
    setVeiculoTms({ show: true, registro, campo, placa: registro[campo] || '' });
  }, [cadastroTms.habilitado]);

  const handleFecharVeiculoTms = useCallback(() => setVeiculoTms(VEICULO_TMS_FECHADO), []);

  const handleSalvarVeiculoTms = useCallback(async ({ uf, renavam, terceiro }) => {
    const { registro, campo, placa } = veiculoTms;
    setVeiculoTms(VEICULO_TMS_FECHADO);
    if (!registro) return;
    await enviarAoTms(campo === 'placa_cavalo' ? 'Cavalo' : 'Carreta', () => cadastrarVeiculoTms(registro.id, { campo, placa, uf, renavam, terceiro: terceiro !== false }), registro);
  }, [enviarAoTms, veiculoTms]);

  const atualizarRegistroNaTela = useCallback((registro, alteracoes) => {
    setTurnos(previous => ({
      ...previous,
      [registro.turno]: {
        ...previous[registro.turno],
        registros: previous[registro.turno].registros.map(item => (item.id === registro.id ? { ...item, ...alteracoes } : item))
      }
    }));
  }, []);

  // Observação do operador: grava só ela (vazia apaga) e atualiza a linha na tela, sem recarregar o turno.
  const handleAtualizarObservacao = useCallback(async (registro, valor) => {
    if (!registro?.id) return;
    const observacao = String(valor || '').trim();
    if (observacao === String(registro.observacao_operador || '').trim()) {
      setInlineEditingCell(null);
      return;
    }

    setUpdatingKey(`${registro.id}:${CAMPO_OBSERVACAO}`);
    try {
      await atualizarObservacaoProgramacaoHavan(registro.id, observacao || null);
      atualizarRegistroNaTela(registro, { observacao_operador: observacao || null });
      notification.success(observacao ? 'Observação salva.' : 'Observação apagada.');
    } catch (error) {
      notification.error(getErrorMessage(error));
    } finally {
      setUpdatingKey(null);
      setInlineEditingCell(null);
    }
  }, [atualizarRegistroNaTela]);

  // Marcação otimista: a linha pinta na hora e volta ao estado anterior se a gravação falhar.
  const handleAlternarTerceiro = useCallback(async (registro, terceiro) => {
    if (!registro?.id || terceiroSalvandoIds.includes(registro.id)) return;
    setTerceiroSalvandoIds(atual => [...atual, registro.id]);
    atualizarRegistroNaTela(registro, { terceiro });
    try {
      await atualizarProgramacaoHavan(registro.id, criarPayloadAtualizacao(registro, 'terceiro', null, { terceiro }));
    } catch (error) {
      atualizarRegistroNaTela(registro, { terceiro: !terceiro });
      notification.error(`Não foi possível ${terceiro ? 'liberar' : 'remover a liberação da'} carga ${registro.ct || ''} para terceiro: ${getErrorMessage(error)}`);
    } finally {
      setTerceiroSalvandoIds(atual => atual.filter(id => id !== registro.id));
    }
  }, [atualizarRegistroNaTela, terceiroSalvandoIds]);

  // Gestor avisado pelo WhatsApp: a linha fica verde forte na hora e a API grava quem avisou e quando
  // (avisar de novo atualiza). Se a gravação falhar, a linha volta como estava e o operador é avisado.
  const marcarGestorAvisado = useCallback(async registro => {
    if (!registro?.id) return;
    const anterior = { gestor_avisado: Boolean(registro.gestor_avisado), gestor_avisado_em: registro.gestor_avisado_em ?? null, gestor_avisado_por: registro.gestor_avisado_por ?? null };
    atualizarRegistroNaTela(registro, { gestor_avisado: true, gestor_avisado_em: new Date().toISOString() });
    try {
      await atualizarGestorAvisadoProgramacaoHavan(registro.id, true);
    } catch (error) {
      atualizarRegistroNaTela(registro, anterior);
      notification.error(`Não foi possível registrar o aviso ao gestor da carga ${registro.ct || ''}: ${getErrorMessage(error)}`);
    }
  }, [atualizarRegistroNaTela]);

  // Protocolo: mesma marcação otimista do terceiro (a linha fica roxa na hora), gravada pelo endpoint próprio.
  const handleAlternarProtocolo = useCallback(async (registro, protocolo) => {
    if (!registro?.id || protocoloSalvandoIds.includes(registro.id)) return;
    setProtocoloSalvandoIds(atual => [...atual, registro.id]);
    atualizarRegistroNaTela(registro, { protocolo });
    try {
      await atualizarProtocoloProgramacaoHavan(registro.id, protocolo);
    } catch (error) {
      atualizarRegistroNaTela(registro, { protocolo: !protocolo });
      notification.error(`Não foi possível ${protocolo ? 'marcar' : 'desmarcar'} o protocolo da carga ${registro.ct || ''}: ${getErrorMessage(error)}`);
    } finally {
      setProtocoloSalvandoIds(atual => atual.filter(id => id !== registro.id));
    }
  }, [atualizarRegistroNaTela, protocoloSalvandoIds]);

  const recarregarTurnoDoRegistro = useCallback(
    registro => carregarTurno(registro.turno === 'segundo' ? 2 : 1, selectedDate, turnos[registro.turno]?.pagina || 1),
    [carregarTurno, selectedDate, turnos]
  );

  // Tipos de mercadoria (VX) e faixas de valor: carregados uma vez, na primeira abertura de modal que precisar.
  const carregarOpcoesCargaVx = useCallback(async () => {
    if (vxOpcoesCarga.data || vxOpcoesCarga.carregando) return;
    setVxOpcoesCarga({ data: null, carregando: true, erro: '' });
    try {
      setVxOpcoesCarga({ data: await buscarOpcoesCargaVxProgramacaoHavan(), carregando: false, erro: '' });
    } catch (error) {
      setVxOpcoesCarga({ data: null, carregando: false, erro: `Não foi possível carregar os tipos de mercadoria da VX: ${getErrorMessage(error)}` });
    }
  }, [vxOpcoesCarga.carregando, vxOpcoesCarga.data]);

  const abrirPesquisaVx = useCallback(async (registro, erro = '') => {
    carregarOpcoesCargaVx();
    setVxPesquisa({ show: true, row: registro, data: null, erro });
    try {
      const data = await buscarPreparacaoVxProgramacaoHavan(registro.id);
      setVxPesquisa({ show: true, row: registro, data, erro });
    } catch (error) {
      setVxPesquisa({ show: false, row: null, data: null, erro: '' });
      notification.error(getErrorMessage(error));
    }
  }, [carregarOpcoesCargaVx]);

  // Um clique: consulta a VX; só pede dados ao usuário quando realmente precisa criar ou renovar.
  const handleAbrirConsultaVx = useCallback(async registro => {
    if (!registro?.id || vxLoading) return;
    setVxLoading(true);
    setVxConsultandoId(registro.id);
    try {
      const response = await consultarVxProgramacaoHavan(registro.id, { criar_pesquisa: false });
      if (response?.acao_executada === 'consulta-nao-encontrada') {
        await abrirPesquisaVx(registro);
      } else if (response?.acao_executada === 'consulta-encontrada-sem-laudo') {
        carregarOpcoesCargaVx();
        setVxModal({ show: true, row: registro, reason: response.mensagem });
      } else {
        setVxResult({ row: registro, response });
        await recarregarTurnoDoRegistro(registro);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      if (error?.response?.status === 502) setVxUnavailable({ show: true, row: registro, message });
      else notification.error(message);
    } finally {
      setVxLoading(false);
      setVxConsultandoId(null);
    }
  }, [abrirPesquisaVx, carregarOpcoesCargaVx, recarregarTurnoDoRegistro, vxLoading]);

  const handleFecharConsultaVx = useCallback(() => {
    if (!vxLoading) setVxModal({ show: false, row: null, reason: '' });
  }, [vxLoading]);

  // Renovação de uma pesquisa vencida do mesmo conjunto.
  const handleEnviarConsultaVx = useCallback(async dados => {
    const registro = vxModal.row;
    if (!registro?.id || vxLoading) return;

    setVxLoading(true);
    try {
      const response = await consultarVxProgramacaoHavan(registro.id, {
        tipo_mercadoria: dados.tipo_mercadoria,
        valor: dados.valor,
        valor_carga: dados.valor_carga
      });
      setVxModal({ show: false, row: null, reason: '' });
      setVxResult({ row: registro, response });
      notification.success('Consulta VX renovada.');
    } catch (error) {
      const message = getErrorMessage(error);
      setVxModal({ show: false, row: null, reason: '' });
      if (error?.response?.status === 502) setVxUnavailable({ show: true, row: registro, message });
      else notification.error(message);
    } finally {
      setVxLoading(false);
      await recarregarTurnoDoRegistro(registro);
    }
  }, [recarregarTurnoDoRegistro, vxLoading, vxModal.row]);

  const handleFecharPesquisaVx = useCallback(() => {
    if (!vxLoading) setVxPesquisa({ show: false, row: null, data: null, erro: '' });
  }, [vxLoading]);

  const handleEnviarPesquisaVx = useCallback(async ({ tipo_mercadoria, valor, valor_carga, motorista, cavalo, carretas, arquivos }) => {
    const registro = vxPesquisa.row;
    if (!registro?.id || vxLoading) return;

    setVxLoading(true);
    try {
      const documentos = await Promise.all(Object.entries(arquivos || {}).map(async ([codigo, file]) => ({
        codigo,
        nome_arquivo: file.name,
        content_type: file.type || 'application/octet-stream',
        base64: await readFileAsBase64(file)
      })));
      const response = await consultarVxProgramacaoHavan(registro.id, { criar_pesquisa: true, tipo_mercadoria, valor, valor_carga, motorista, cavalo, carretas, documentos });
      setVxPesquisa({ show: false, row: null, data: null, erro: '' });
      setVxResult({ row: registro, response });
      notification.success('Consulta VX criada.');
      await recarregarTurnoDoRegistro(registro);
    } catch (error) {
      const message = getErrorMessage(error);
      if (error?.response?.status === 502) {
        setVxPesquisa({ show: false, row: null, data: null, erro: '' });
        setVxUnavailable({ show: true, row: registro, message });
      } else if (error?.response?.status === 400 && !/document|comprovante|anex/i.test(message)) {
        // Erro de dado: o modal continua aberto com tudo o que foi digitado e anexado.
        setVxPesquisa(atual => ({ ...atual, erro: message }));
      } else if (error?.response?.status === 400) {
        // Pendência de documento: recarrega os documentos exigidos mantendo o que foi digitado.
        try {
          const data = await buscarPreparacaoVxProgramacaoHavan(registro.id);
          setVxPesquisa(atual => ({
            ...atual,
            erro: message,
            data: { ...data, motorista, cavalo, carretas }
          }));
        } catch (preparacaoError) {
          setVxPesquisa(atual => ({ ...atual, erro: message }));
        }
      } else {
        notification.error(message);
      }
    } finally {
      setVxLoading(false);
    }
  }, [recarregarTurnoDoRegistro, vxLoading, vxPesquisa.row]);

  const handleFecharResultadoVx = useCallback(() => setVxResult(null), []);

  const handleFecharVxIndisponivel = useCallback(() => setVxUnavailable({ show: false, row: null, message: '' }), []);

  const handleAbrirPendenciaVx = useCallback(registro => {
    if (registro?.id && !vxLoading) abrirPesquisaVx(registro, registro.vx_mensagem || '');
  }, [abrirPesquisaVx, vxLoading]);

  const handleBaixarLaudoVx = useCallback(async registro => {
    if (!registro?.id || vxBaixandoLaudoId) return;
    setVxBaixandoLaudoId(registro.id);
    try {
      const { blob, nomeArquivo } = await baixarLaudoVxProgramacaoHavan(registro.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Com responseType blob o corpo de erro também chega como Blob.
      let mensagem = null;
      try {
        const corpo = error?.response?.data;
        if (corpo instanceof Blob) mensagem = JSON.parse(await corpo.text())?.error;
      } catch (e) {
        mensagem = null;
      }
      notification.error(mensagem || getErrorMessage(error));
    } finally {
      setVxBaixandoLaudoId(null);
    }
  }, [vxBaixandoLaudoId]);

  const handleRepetirConsultaVx = useCallback(() => {
    const registro = vxUnavailable.row;
    setVxUnavailable({ show: false, row: null, message: '' });
    if (registro) handleAbrirConsultaVx(registro);
  }, [handleAbrirConsultaVx, vxUnavailable.row]);

  const inlineEditConfig = useMemo(() => ({
    editingCellKey: inlineEditingCell ? `${inlineEditingCell.id}:${inlineEditingCell.campo}` : null,
    savingCellKey: updatingKey,
    editingInitialValue: inlineEditingCell?.valorOriginal || '',
    popoverPosition: inlineEditingCell?.popoverPosition || null,
    startEdit: handleIniciarEdicaoInline,
    commitEdit: ({ registro, campo, value }) => (campo === CAMPO_OBSERVACAO ? handleAtualizarObservacao(registro, value) : handleAtualizarCampo(registro, campo, value)),
    cancelEdit: handleCancelarEdicaoInline,
    searchOptions: handleBuscarOpcoesEdicao,
    selectOption: handleSelecionarOpcaoEdicao,
    toggleTerceiro: handleAlternarTerceiro,
    toggleProtocolo: handleAlternarProtocolo,
    marcarGestorAvisado,
    clearField: (campo, registro) => (campo === CAMPO_OBSERVACAO ? handleAtualizarObservacao(registro, '') : handleRemoverCampo(campo, registro)),
    useForaTms: handleUsarForaTms,
    cadastroTms: { ...cadastroTms, abrir: handleAbrirCadastroTms },
    terceiroSalvandoIds,
    protocoloSalvandoIds
  }), [cadastroTms, marcarGestorAvisado, handleAbrirCadastroTms, handleAlternarProtocolo, handleAlternarTerceiro, handleAtualizarCampo, handleAtualizarObservacao, handleRemoverCampo, handleUsarForaTms, handleBuscarOpcoesEdicao, handleCancelarEdicaoInline, handleIniciarEdicaoInline, handleSelecionarOpcaoEdicao, inlineEditingCell, protocoloSalvandoIds, terceiroSalvandoIds, updatingKey]);

  const vxConfig = useMemo(() => ({ onConsultar: handleAbrirConsultaVx, onAbrirDocumentos: handleAbrirPendenciaVx, onBaixarLaudo: handleBaixarLaudoVx, consultandoId: vxConsultandoId, baixandoLaudoId: vxBaixandoLaudoId }), [handleAbrirConsultaVx, handleAbrirPendenciaVx, handleBaixarLaudoVx, vxBaixandoLaudoId, vxConsultandoId]);

  return {
    dateOptions,
    selectedDate,
    selectedDateOption,
    turnoAtivo,
    turnos,
    importModal,
    inlineEditConfig,
    totalRegistros: turnos.primeiro.totalRegistros + turnos.segundo.totalRegistros,
    onSelectDate: handleSelectDate,
    onPreviousDate: handlePreviousDate,
    onNextDate: handleNextDate,
    onTurnoFilter: handleTurnoFilter,
    onChangePage: handleChangePage,
    onRetryTurno: handleRetryTurno,
    // Depois de incluir uma carga manualmente: recarrega as datas (totais) e os turnos da data que está na tela.
    onRecarregarDataSelecionada: () => recarregarAposImportacao(selectedDateRef.current),
    onOpenImportModal: handleOpenImportModal,
    onCloseImportModal: handleCloseImportModal,
    onImportarArquivo: handleImportarArquivo,
    onPrevisualizarDefinitiva: handlePrevisualizarDefinitiva,
    onAplicarDefinitiva: handleAplicarDefinitiva,
    vxModal,
    vxLoading,
    vxResult,
    vxUnavailable,
    vxPesquisa,
    vxOpcoesCarga,
    vxConfirmacaoAlteracao,
    motoristaForaTms,
    onFecharMotoristaForaTms: handleFecharMotoristaForaTms,
    onSalvarMotoristaForaTms: handleSalvarMotoristaForaTms,
    veiculoTms,
    cadastroTms,
    onFecharVeiculoTms: handleFecharVeiculoTms,
    onSalvarVeiculoTms: handleSalvarVeiculoTms,
    onCancelarAlteracaoComVx: handleCancelarAlteracaoComVx,
    onConfirmarAlteracaoComVx: handleConfirmarAlteracaoComVx,
    vxConfig,
    onCloseVxModal: handleFecharConsultaVx,
    onSubmitVx: handleEnviarConsultaVx,
    onCloseVxResult: handleFecharResultadoVx,
    onCloseVxUnavailable: handleFecharVxIndisponivel,
    onRetryVx: handleRepetirConsultaVx,
    onCloseVxPesquisa: handleFecharPesquisaVx,
    onSubmitVxPesquisa: handleEnviarPesquisaVx,
    onBaixarLaudoVx: handleBaixarLaudoVx,
    vxBaixandoLaudoId
  };
};
